import { z } from "zod";

import { machineTypeSchema } from "../machine-type";
import { machineWithCorrugateTracksWithToleranceSchema } from "../machine-with-corrugate-tracks";
import { unitOfMeasureSchema } from "../unit-of-measure";

export const x7MachineSchema = machineWithCorrugateTracksWithToleranceSchema.extend({
	machineType: z.literal(machineTypeSchema.enum.X7),
	// TODO @eb remove as soon as possible. Unit of measure should be taken from tenant and that information should not be needed on the machines
	unitOfMeasure: unitOfMeasureSchema,
});

// TODO @eb remove this function when the X7 aligns with our decided upon schemas
export const prepareX7MachineData = (data: unknown): unknown => {
	if (typeof data !== "object" || data === null) {
		return data;
	}

	const input = data as Record<string, unknown>;
	const securePrintConfigurationInput = input.securePrintConfiguration as { delayLabelRetrieval?: boolean } | undefined;

	const iotDevice = {
		hubName: input.iotHubName ?? "",
		deviceId: input.iotEdgeDeviceId ?? "",
		moduleId: input.iotEdgeModuleId ?? "",
		moduleVersion: input.edgeModuleVersion ?? "",
		lastProofOfLife: input.proofOfLifeUpdated ?? new Date(0),
	};

	let unitOfMeasure = input.unitOfMeasure;
	if (input.unitOfMeasurement === "UsCustomary") {
		unitOfMeasure = "Imperial";
	} else if (input.unitOfMeasurement === "Metric") {
		unitOfMeasure = "Metric";
	}

	let tracks = input.tracks;
	if (Array.isArray(tracks)) {
		tracks = tracks
			.map((t) => {
				if (typeof t !== "object" || t === null) {
					return t;
				}

				const track = t as Record<string, unknown>;

				let assignedMaterial = track.loadedCorrugate;
				if (typeof assignedMaterial === "object" && assignedMaterial !== null) {
					const material = assignedMaterial as Record<string, unknown>;

					assignedMaterial = {
						id: material.id ?? "00000000-0000-0000-0000-000000000000",
						alias: material.alias ?? "",
						width: material.width ?? track.corrugateWidthMm ?? 0,
						thickness: 0,
						quality: 0,
					};
				} else {
					// If assignedMaterial is null, create default
					assignedMaterial = {
						id: "00000000-0000-0000-0000-000000000000",
						alias: "",
						width: 0,
						thickness: 0,
						quality: 0,
					};
				}

				if (track.canAcceptCorrugate === false) {
					return undefined;
				}

				return {
					number: track.trackNumber,
					isEmpty: track.corrugatePresent === false,
					assignedMaterial,
					corrugateTolerance: 5,
					width: track.corrugateWidthMm,
				};
			})
			.filter((t) => t !== undefined);
	}

	const machineStopReason = input.stopReason as Record<string, unknown>;
	let stopReason = { stopCode: 0, message: "", arguments: [], isError: false };
	if (typeof machineStopReason === "object" && machineStopReason !== null) {
		stopReason = {
			stopCode: machineStopReason.alarmType as number,
			message: (machineStopReason.message as string) ?? "",
			arguments: [],
			isError: machineStopReason.isError === true,
		};
	}

	let activeWarnings = Array.isArray(input.warnings) ? input.warnings : [];
	activeWarnings = activeWarnings.map((w: Record<string, unknown>) => ({
		message: w.message,
		code: w.alarmType,
		arguments: [],
	}));

	let workstations = input.workstations;
	if (!Array.isArray(workstations) || workstations.length < 1) {
		workstations = [
			{
				number: 1,
				alarms: [
					{
						code: stopReason.stopCode,
						message: stopReason.message,
						arguments: stopReason.arguments.map(String),
					},
				],
				warnings: activeWarnings.map((w: Record<string, unknown>) => ({
					...w,
					arguments: Array.isArray(w.arguments) ? w.arguments.map(String) : [],
				})),
				stopReason: {
					code: stopReason.stopCode,
					message: stopReason.message,
					arguments: stopReason.arguments.map(String),
					isError: stopReason.isError,
				},
			},
		];
	}

	return {
		...input,
		machineType: "X7",
		assetId: input.assetId?.toString() ?? "",
		description: input.description ?? "",
		plcVersion: input.plcVersion ?? "",
		unitOfMeasure,
		iotDevice,
		stopReason,
		activeWarnings,
		tracks,
		securePrintConfiguration: {
			delayLabelRetrieval: securePrintConfigurationInput?.delayLabelRetrieval ?? false,
		},
		workstations,
	};
};

export type X7Machine = z.infer<typeof x7MachineSchema>;
